import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { MessageCircle, Plus, Trash2, Search, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { adminAPI, productsAPI, ordersAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

export default function AdminCreateOrder() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [notes, setNotes] = useState('');
    const [orderItems, setOrderItems] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [prefillApplied, setPrefillApplied] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const [clientsRes, productsRes] = await Promise.all([
                    adminAPI.getClients({ limit: 500 }),
                    productsAPI.getAll({ limit: 500 }),
                ]);
                const loadedClients = clientsRes.data.clients || clientsRes.data;
                const loadedProducts = productsRes.data.products || productsRes.data;
                setClients(loadedClients);
                setProducts(loadedProducts);
                
                // Apply prefill from WhatsApp link
                const refCode = searchParams.get('ref');
                if (refCode && !prefillApplied) {
                    try {
                        const prefillRes = await ordersAPI.getPrefill(refCode);
                        const data = prefillRes.data;
                        
                        if (data.client_email) {
                            const client = loadedClients.find(c => c.email === data.client_email);
                            if (client) setSelectedClient(client.id);
                        }
                        if (data.items?.length) {
                            setOrderItems(data.items.map(i => ({
                                product_id: i.id, product_name: i.name,
                                quantity: i.qty, price: i.price, unit: i.unit, image_url: i.img || '',
                            })));
                        }
                        if (data.address) setDeliveryAddress(data.address);
                        if (data.notes) setNotes(data.notes);
                        setPrefillApplied(true);
                        toast.success('Commande WhatsApp pré-remplie !');
                    } catch (err) { console.error('Invalid prefill data:', err); }
                }
            } catch (err) {
                console.error('AdminCreateOrder load error:', err);
                toast.error('Erreur lors du chargement');
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount, prefill from searchParams
    }, [searchParams, prefillApplied]);

    const selectedClientData = clients.find(c => c.id === selectedClient);

    useEffect(() => {
        if (selectedClientData) {
            setDeliveryAddress(selectedClientData.address || '');
        }
    }, [selectedClientData]);

    const filteredProducts = products.filter(p =>
        p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.origin?.toLowerCase().includes(productSearch.toLowerCase())
    );

    const addProduct = (product) => {
        if (orderItems.find(i => i.product_id === product.id)) {
            toast.info('Produit déjà ajouté');
            return;
        }
        setOrderItems(prev => [...prev, {
            product_id: product.id,
            product_name: product.name,
            quantity: 1,
            price: product.price,
            unit: product.unit,
            image_url: product.image_url || '',
        }]);
        setProductSearch('');
    };

    const updateItem = (idx, field, value) => {
        setOrderItems(prev => prev.map((item, i) =>
            i === idx ? { ...item, [field]: Number(value) } : item
        ));
    };

    const removeItem = (idx) => {
        setOrderItems(prev => prev.filter((_, i) => i !== idx));
    };

    const total = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedClient) return toast.error('Sélectionnez un client');
        if (orderItems.length === 0) return toast.error('Ajoutez au moins un produit');
        if (!deliveryAddress.trim()) return toast.error('Renseignez l\'adresse de livraison');

        setSaving(true);
        try {
            const res = await adminAPI.createOrder({
                client_id: selectedClient,
                items: orderItems.map(i => ({
                    product_id: i.product_id,
                    product_name: i.product_name,
                    quantity: i.quantity,
                    price: i.price,
                    unit: i.unit,
                    subtotal: i.price * i.quantity,
                    image_url: i.image_url || '',
                })),
                delivery_address: deliveryAddress,
                notes: notes || null,
            });
            toast.success(`Commande ${res.data.order_number} créée avec succès !`);
            navigate('/admin/commandes');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-create-order">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-jungle/10">
                        <MessageCircle className="h-6 w-6 text-jungle" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">Créer une commande</h1>
                        <p className="text-sm text-muted-foreground">Saisissez une commande reçue par WhatsApp</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left column: client + address */}
                    <div className="space-y-4">
                        <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                            <CardHeader><CardTitle className="text-jungle text-lg">Client</CardTitle></CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Sélectionner le client</Label>
                                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                                        <SelectTrigger data-testid="select-client">
                                            <SelectValue placeholder="Choisir un client..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {clients.map(c => (
                                                <SelectItem key={c.id} value={c.id}>
                                                    {c.company_name} - {c.contact_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {selectedClientData && (
                                    <div className="p-3 rounded-lg bg-jungle/5 text-sm space-y-1">
                                        <p><strong>{selectedClientData.company_name}</strong></p>
                                        <p>{selectedClientData.contact_name}</p>
                                        <p>{selectedClientData.phone}</p>
                                        <p>{selectedClientData.email}</p>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label>Adresse de livraison</Label>
                                    <Input
                                        value={deliveryAddress}
                                        onChange={(e) => setDeliveryAddress(e.target.value)}
                                        placeholder="Adresse..."
                                        data-testid="delivery-address"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Notes</Label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Notes de commande..."
                                        data-testid="order-notes"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Middle column: product search + add */}
                    <div className="space-y-4">
                        <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                            <CardHeader><CardTitle className="text-jungle text-lg">Ajouter des produits</CardTitle></CardHeader>
                            <CardContent className="space-y-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher un produit..."
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                        className="pl-10"
                                        data-testid="product-search"
                                    />
                                </div>
                                <div className="max-h-[400px] overflow-y-auto space-y-1">
                                    {(productSearch ? filteredProducts : products).slice(0, 20).map(p => (
                                        <div key={p.id}
                                            className="flex items-center justify-between p-2 rounded-lg hover:bg-jungle/5 cursor-pointer transition-colors"
                                            onClick={() => addProduct(p)}>
                                            <div className="flex items-center gap-2">
                                                {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                                                <div>
                                                    <p className="text-sm font-medium">{p.name}</p>
                                                    <p className="text-xs text-muted-foreground">{formatPrice(p.price)}/{p.unit}</p>
                                                </div>
                                            </div>
                                            <Plus className="h-4 w-4 text-leaf" />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right column: order summary */}
                    <div className="space-y-4">
                        <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                            <CardHeader><CardTitle className="text-jungle text-lg">Récapitulatif</CardTitle></CardHeader>
                            <CardContent>
                                {orderItems.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-6">Aucun produit ajouté</p>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Produit</TableHead>
                                                <TableHead className="w-20">Qté</TableHead>
                                                <TableHead className="w-20">Prix</TableHead>
                                                <TableHead className="w-8"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {orderItems.map((item) => (
                                                <TableRow key={item.product_id}>
                                                    <TableCell className="text-sm">{item.product_name}</TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                                            className="h-8 w-16 text-sm"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="text-sm">{formatPrice(item.price * item.quantity)}</TableCell>
                                                    <TableCell>
                                                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                                                            <Trash2 className="h-3 w-3 text-red-500" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                                    <span className="font-bold text-jungle text-lg">TOTAL</span>
                                    <span className="font-bold text-jungle text-lg">{formatPrice(total)}</span>
                                </div>
                                <Button
                                    type="submit"
                                    disabled={saving || orderItems.length === 0 || !selectedClient}
                                    className="w-full mt-4 bg-leaf hover:bg-leaf/90 text-white"
                                    data-testid="submit-order"
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    {saving ? 'Création...' : 'Créer la commande'}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </form>
            </div>
        </div>
    );
}
